import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { SigninWithCredentialsOptions, isAuthApiError } from '@okta/okta-auth-js';
import { UserService, RouterExtService } from '@app/services/_index';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  returnUrl: string;
  // NOTE: We only allow the following route slugs for redirect for security
  allowRedirectRoutingList = ['course', 'subscription', 'home', 'dashboard', 'search'];

  invalidCredentials = 'Invalid username or password';
  defaultErrorMessage = this.invalidCredentials.slice();

  loginForm: FormGroup;
  showLoginError = false; // Show Login error for invalid password or user name
  invalid = false; // Use to toggle the invalid input state styling
  isCookieSet = false; // Use this to check if the Docebo token is set in the cookie
  environment: any;
  isAuthenticating = true; // TODO: Need to fix this in the UI template
  isAuthenticated$: Observable<boolean>;

  showOktaLoginButton = environment.beyond.featureFlags.showOktaLoginButton;

  constructor(
    private router: Router,
    private routerExt: RouterExtService,
    private userService: UserService
  ) {
    this.isAuthenticated$ = this.userService.isAuthenticated$;

    this.loginForm = new FormGroup({
      username: new FormControl(''),
      password: new FormControl(''),
    });
  }

  /**
   * OnInit calls the authservice to check if he JWT cookie exists.
   * If the JWT Cookie exists call the auth service authenticate JWT to get
   * get the docebo token and set it in the user's local session storage
   */
  ngOnInit(): void {
    // Set environment to set external links like Agent Platform in the login.component.html
    this.environment = environment;
    // Get previous url so that we can return to it after logging in
    this.returnUrl = this.routerExt.getPreviousUrl();
    // If previous url is the login page or main homepage, redirect to dashboard
    if (['/login', '/'].includes(this.returnUrl)) {
      this.returnUrl = 'dashboard';
    }
  }

  /**
   * Process user name and password on login event to be
   * authenticated with OKTA
   */
  async signInWithCredentials(): Promise<void> {
    // Reset any invalid error messages and styling
    this.resetLoginErrors();

    const options: SigninWithCredentialsOptions = {
      username: this.loginForm.value.username,
      password: this.loginForm.value.password,
    };

    if (this.loginForm.value.username && this.loginForm.value.password) {
      return this.userService
        .signInWithCredentials(options)
        .then(() => {
          // Signing in with username / password requires actual navigation redirect
          this.router.navigateByUrl(this.returnUrl || 'dashboard');
        })
        .catch((err) => {
          this.showLoginErrorandReset();
          this.defaultErrorMessage = this.formatErrorMessage(err);
          console.error(err);
        });
    } else {
      this.showLoginErrorandReset();
      this.defaultErrorMessage = this.invalidCredentials;
    }
  }

  /**
   * "Sign In with Okta" and redirect
   */
  signInWithRedirect(): void {
    this.resetLoginErrors();
    this.userService.signInWithRedirect(this.returnUrl).catch((err) => {
      this.showLoginErrorandReset();
      this.defaultErrorMessage = this.formatErrorMessage(err);
    });
  }

  /**
   * Reset Login Credential related error flags and messages.
   */
  resetLoginErrors(): void {
    this.showLoginError = false;
    this.invalid = false;
    this.isAuthenticating = true;
    this.defaultErrorMessage = this.invalidCredentials.slice();
  }

  /**
   * Show Login Errors and Reset Login form credentials
   */
  showLoginErrorandReset(): void {
    this.showLoginError = true;
    // this.isAuthenticating = false;
    this.invalid = true;
    this.loginForm.reset();
  }

  /**
   * Format and show login user a simple message to check Okta or use Sign In with Okta option
   * if Okta custom sign in fails due to Okta verification.
   * @param error - Okta Service error message
   */
  formatErrorMessage(error: Error): string {
    console.log('ERROR', error);
    if (error.message.includes('MFA_ENROLL')) {
      return `Multi-Factor Authentication enrollment required. Please contact <a class="gen-primary-link--accesible with-underline" href="mailto:beyond@genesys.com">
      beyond@genesys.com </a>`;
    } else if (error.message.includes('MFA_CHALLENGE')) {
      return `Multi-Factor Authentication required. Please use <b>Sign In with Okta</b> below.`;
    } else if (isAuthApiError(error)) {
      return this.invalidCredentials;
    } else {
      return this.defaultErrorMessage;
    }
  }

  /**
   * Sign Out of Okta
   */
  signOut(): void {
    this.userService.signOut();
  }
}
